import { Injectable, inject } from '@angular/core';
import { Router, UrlTree, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { CompanyBillingService } from '../../features/company-billing/services/company-billing.service';
import { BillingStatus } from '../enums/billing-status.enum';
import { firstValueFrom } from 'rxjs';

/**
 * Guard que verifica si la empresa tiene un plan activo.
 * Si no tiene plan o su estado no es ACTIVE, redirige a la página de selección de plan.
 */
@Injectable({ providedIn: 'root' })
export class BillingStatusGuard {
  private authService = inject(AuthenticationService);
  private billingService = inject(CompanyBillingService);
  private router = inject(Router);

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    if (state.url.startsWith('/select-plan')) {
      return true;
    }

    const user = this.authService.getUser();
    const companyId = user?.company?.id ?? user?.companyId;

    // Si no hay companyId, permitir pasar (probablemente en login)
    if (!companyId) {
      return true;
    }

    try {
      const billing = await firstValueFrom(
        this.billingService.getByCompany(companyId)
      );

      // Solo una respuesta verificada activa o de prueba permite acceso.
      if (billing && (billing.billingStatus === BillingStatus.ACTIVE || billing.billingStatus === BillingStatus.TRIAL_ACTIVE)) {
        return true;
      }

      return this.router.createUrlTree(['/select-plan'], {
        queryParams: { returnUrl: state.url, verification: billing ? 'inactive' : 'unavailable' }
      });

    } catch (error) {
      console.error('[BillingStatusGuard] Error checking billing status:', error);
      // No asumir facturación activa si no se puede verificar.
      return this.router.createUrlTree(['/select-plan'], { queryParams: { returnUrl: state.url, verification: 'unavailable' } });
    }
  }
}

/**
 * Función guard para usar en rutas (Angular 17+)
 */
export const billingStatusGuard: CanActivateFn = (route, state) => {
  const guard = inject(BillingStatusGuard);
  return guard.canActivate(route, state);
};
